import React from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Box, FormHelperText } from '@material-ui/core';
import { OptionTypeBase, OptionsType } from 'react-select';

import { BrandedGCCurrency } from 'features/my-gift-cards/ts/BrandedGCCurrency';
import { SelectInput, SelectOption } from 'features/app/components/SelectInput';
import { useTranslation } from 'hooks/useTranslation';
import { getCurrenciesRequest } from 'features/my-gift-cards/api';

interface CurrencySelectorProps {
  name: string;
}

const fetchCurrencies = async (_: string) => {
  const { data } = await getCurrenciesRequest(_);
  return data.map(currency => ({
    label: currency.currency_name as string,
    value: currency.currency_id as string,
    ...currency,
  }));
};

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ name }) => {
  const { control } = useFormContext();
  const { t } = useTranslation('my-gift-cards');

  const {
    field: { value: _value, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const value = _value as BrandedGCCurrency[];

  const selectValue = value?.map(({ currency_id, currency_name, ...rest }) => ({
    value: currency_id as string,
    label: currency_name as string,
    ...rest,
  }));

  const handleSelectChange = React.useCallback(
    (options: OptionTypeBase | OptionsType<OptionTypeBase> | null) => {
      onChange(
        (options as SelectOption[]).map(({ label, value, ...rest }) => ({
          currency_id: value,
          currency_name: label,
          ...rest,
        }))
      );
    },
    [onChange]
  );

  return (
    <Box marginTop={5}>
      <SelectInput
        label={t('currency')}
        name={name}
        value={selectValue}
        onChange={handleSelectChange}
        fetchItems={fetchCurrencies}
        isMulti={true}
        isAsync={true}
        isClearable={false}
        placeholder={t('currencyPlaceholder')}
      />
      {error?.message && <FormHelperText error>{error?.message}</FormHelperText>}
    </Box>
  );
};
